import type {Comment, Thread} from '@github-ui/diff-lines/conversation-types'

export function getComments(thread: Thread): Comment[] {
  return thread.commentsData?.comments ?? []
}

export function isPendingComment(comment: Comment): boolean {
  return comment.state.toLowerCase() === 'pending'
}

export function getSubmittedComments(thread: Thread): Comment[] {
  return getComments(thread).filter(comment => !isPendingComment(comment))
}

export function getPendingComments(thread: Thread): Comment[] {
  return getComments(thread).filter(comment => isPendingComment(comment))
}

/** The first comment in the thread. */
export function getInitialComment(thread: Thread): Comment | undefined {
  return getComments(thread)[0]
}

/** The most recent comment in the thread (the initial comment when there are no replies). */
export function getLatestSubmittedComment(thread: Thread): Comment | undefined {
  return getSubmittedComments(thread).at(-1)
}

/** The most recent submitted comment, but only when it is a reply (i.e. not the initial comment). */
export function getLatestSubmittedReply(thread: Thread): Comment | undefined {
  const submittedComments = getSubmittedComments(thread)
  return submittedComments.length > 1 ? submittedComments.at(-1) : undefined
}

const COMMENT_SNIPPET_LENGTH = 100

/**
 * Gets a snippet of the comment text, truncated to the maximum length.
 * While `text-overflow: ellipsis` will handle dynamic truncation, we still want to limit the length of the rendered
 * string for accessibility and performance.
 */
export function getCommentSnippet(comment: Comment): string {
  const normalized = comment.body.replace(/\s+/g, ' ').trim()
  const chars = Array.from(normalized)
  return chars.length > COMMENT_SNIPPET_LENGTH ? `${chars.slice(0, COMMENT_SNIPPET_LENGTH).join('')}…` : normalized
}
